"use client";

import _toConsumableArray from "/home/runner/work/website/website/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _slicedToArray from "/home/runner/work/website/website/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { jsx, Fragment } from 'react/jsx-runtime';
import { useMemo, useRef, useState, useContext } from 'react';
import { PresenceChild } from './PresenceChild.mjs';
import { LayoutGroupContext } from '../../context/LayoutGroupContext.mjs';
import { invariant } from '../../utils/errors.mjs';
import { useConstant } from '../../utils/use-constant.mjs';
import { onlyElements, getChildKey } from './utils.mjs';
import { useIsomorphicLayoutEffect } from '../../utils/use-isomorphic-effect.mjs';

/**
 * `AnimatePresence` enables the animation of components that have been removed from the tree.
 *
 * When adding/removing more than a single child, every child **must** be given a unique `key` prop.
 *
 * Any `motion` components that have an `exit` property defined will animate out when removed from
 * the tree.
 *
 * ```jsx
 * import { motion, AnimatePresence } from 'framer-motion'
 *
 * export const Items = ({ items }) => (
 *   <AnimatePresence>
 *     {items.map(item => (
 *       <motion.div
 *         key={item.id}
 *         initial={{ opacity: 0 }}
 *         animate={{ opacity: 1 }}
 *         exit={{ opacity: 0 }}
 *       />
 *     ))}
 *   </AnimatePresence>
 * )
 * ```
 *
 * You can sequence exit animations throughout a tree using variants.
 *
 * If a child contains multiple `motion` components with `exit` props, it will only unmount the child
 * once all `motion` components have finished animating out. Likewise, any components using
 * `usePresence` all need to call `safeToRemove`.
 *
 * @public
 */
var AnimatePresence = function AnimatePresence(_ref) {
  var children = _ref.children,
    exitBeforeEnter = _ref.exitBeforeEnter,
    custom = _ref.custom,
    _ref$initial = _ref.initial,
    initial = _ref$initial === void 0 ? true : _ref$initial,
    onExitComplete = _ref.onExitComplete,
    _ref$presenceAffectsL = _ref.presenceAffectsLayout,
    presenceAffectsLayout = _ref$presenceAffectsL === void 0 ? true : _ref$presenceAffectsL,
    _ref$mode = _ref.mode,
    mode = _ref$mode === void 0 ? "sync" : _ref$mode;
  invariant(!exitBeforeEnter, "Replace exitBeforeEnter with mode='wait'");
  /**
   * Filter any children that aren't ReactElements. We can only track components
   * between renders with a props.key.
   */
  var presentChildren = useMemo(function () {
    return onlyElements(children);
  }, [children]);
  /**
   * Track the keys of the currently rendered children. This is used to
   * determine which children are exiting.
   */
  var presentKeys = presentChildren.map(getChildKey);
  /**
   * If `initial={false}` we only want to pass this to components in the first render.
   */
  var isInitialRender = useRef(true);
  /**
   * A ref containing the currently present children. When all exit animations
   * are complete, we use this to re-render the component with the latest children
   * *committed* rather than the latest children *rendered*.
   */
  var pendingPresentChildren = useRef(presentChildren);
  /**
   * Track which exiting children have finished animating out.
   */
  var exitComplete = useConstant(function () {
    return new Map();
  });
  /**
   * Save children to render as React state. To ensure this component is concurrent-safe,
   * we check for exiting children via an effect.
   */
  var _useState = useState(presentChildren),
    _useState2 = _slicedToArray(_useState, 2),
    diffedChildren = _useState2[0],
    setDiffedChildren = _useState2[1];
  var _useState3 = useState(presentChildren),
    _useState4 = _slicedToArray(_useState3, 2),
    renderedChildren = _useState4[0],
    setRenderedChildren = _useState4[1];
  useIsomorphicLayoutEffect(function () {
    isInitialRender.current = false;
    pendingPresentChildren.current = presentChildren;
    /**
     * Update complete status of exiting children.
     */
    for (var i = 0; i < renderedChildren.length; i++) {
      var key = getChildKey(renderedChildren[i]);
      if (!presentKeys.includes(key)) {
        if (exitComplete.get(key) !== true) {
          exitComplete.set(key, false);
        }
      } else {
        exitComplete.delete(key);
      }
    }
  }, [renderedChildren, presentKeys.length, presentKeys.join("-")]);
  var exitingChildren = [];
  if (presentChildren !== diffedChildren) {
    var nextChildren = _toConsumableArray(presentChildren);
    /**
     * Loop through all the currently rendered components and decide which
     * are exiting.
     */
    for (var i = 0; i < renderedChildren.length; i++) {
      var child = renderedChildren[i];
      var key = getChildKey(child);
      if (!presentKeys.includes(key)) {
        nextChildren.splice(i, 0, child);
        exitingChildren.push(child);
      }
    }
    /**
     * If we're in "wait" mode, and we have exiting children, we want to
     * only render these until they've all exited.
     */
    if (mode === "wait" && exitingChildren.length) {
      nextChildren = exitingChildren;
    }
    setRenderedChildren(onlyElements(nextChildren));
    setDiffedChildren(presentChildren);
    /**
     * Early return to ensure once we've set state with the latest diffed
     * children, we can immediately re-render.
     */
    return;
  }
  if (process.env.NODE_ENV !== "production" && mode === "wait" && renderedChildren.length > 1) {
    console.warn("You're attempting to animate multiple children within AnimatePresence, but its mode is set to \"wait\". This will lead to odd visual behaviour.");
  }
  /**
   * If we've been provided a forceRender function by the LayoutGroupContext,
   * we can use it to force a re-render amongst all surrounding components once
   * all components have finished animating out.
   */
  var _useContext = useContext(LayoutGroupContext),
    forceRender = _useContext.forceRender;
  return jsx(Fragment, {
    children: renderedChildren.map(function (child) {
      var key = getChildKey(child);
      var isPresent = presentChildren === renderedChildren || presentKeys.includes(key);
      var onExit = function onExit() {
        if (exitComplete.has(key)) {
          exitComplete.set(key, true);
        } else {
          return;
        }
        var isEveryExitComplete = true;
        exitComplete.forEach(function (isExitComplete) {
          if (!isExitComplete) isEveryExitComplete = false;
        });
        if (isEveryExitComplete) {
          forceRender === null || forceRender === void 0 ? void 0 : forceRender();
          setRenderedChildren(pendingPresentChildren.current);
          onExitComplete && onExitComplete();
        }
      };
      return jsx(PresenceChild, {
        isPresent: isPresent,
        initial: !isInitialRender.current || initial ? undefined : false,
        custom: isPresent ? undefined : custom,
        presenceAffectsLayout: presenceAffectsLayout,
        mode: mode,
        onExitComplete: isPresent ? undefined : onExit,
        children: child
      }, key);
    })
  });
};
export { AnimatePresence };